<template>
  <div class="auth-wrapper auth-v1 px-2">
    <div class="auth-inner py-2 merchant-register-card">
      <b-card title="NEW MERCHANT REGISTRATION">
        <b-overlay
          :show="modalLoading || configLoading"
          blur="10px"
          opacity="0.30"
          rounded="sm"
          variant="light"
        >
          <ValidationObserver
            ref="createMerchantForm"
            v-slot="{ handleSubmit }"
            slim
          >
            <b-form
              class="my-8"
              @submit.prevent="handleSubmit(onClickCreateMerchant)"
            >
              <!-- Dynamic Sections Loop -->
              <div
                v-for="sectionName in visibleSections"
                :key="sectionName"
                class="registration-section mb-3"
              >
                <!-- Section Header -->
                <b-row class="mb-2">
                  <b-col
                    cols="12"
                    class="d-flex justify-content-center"
                  >
                    <div class="d-flex align-items-center mt-1 mb-1">
                      <feather-icon
                        :icon="getSectionIcon(sectionName)"
                        size="19"
                      />
                      <h4 class="mb-0 ml-50 sub-heading font-weight-bolder">
                        {{ getSectionDisplayTitle(sectionName) }}
                      </h4>
                    </div>
                  </b-col>
                </b-row>

                <!-- Section Fields Grid -->
                <b-row>
                  <b-col
                    v-for="field in getEnabledFieldsBySection(sectionName)"
                    :key="field.key"
                    :cols="12"
                    :md="getFieldColSpan(field)"
                  >
                    <!-- Special Case 1: Country Dropdown (Dependent) -->
                    <b-form-group
                      v-if="field.key === 'country_id'"
                      :id="'group-' + field.key"
                      :label-for="field.key"
                    >
                      <template #label>
                        <span>{{ field.label }}</span>
                        <span
                          v-if="field.required"
                          class="text-danger"
                        > *</span>
                      </template>
                      <ValidationProvider
                        #default="{ errors }"
                        :name="field.label"
                        :rules="getFieldValidationRules(field)"
                      >
                        <v-select
                          :id="field.key"
                          v-model="form.country"
                          :options="countryOptions"
                          :reduce="option => option.id"
                          label="name"
                          :placeholder="field.placeholder || 'Select the country'"
                          autocomplete="nope"
                          @input="fetchStateList()"
                        >
                          <template #option="option">
                            {{ option.name }}
                          </template>
                        </v-select>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </ValidationProvider>
                    </b-form-group>

                    <!-- Special Case 2: State Dropdown (Dependent) -->
                    <b-form-group
                      v-else-if="field.key === 'state_id'"
                      :id="'group-' + field.key"
                      :label-for="field.key"
                    >
                      <template #label>
                        <span>{{ field.label }}</span>
                        <span
                          v-if="field.required"
                          class="text-danger"
                        > *</span>
                      </template>
                      <ValidationProvider
                        #default="{ errors }"
                        :name="field.label"
                        :rules="getFieldValidationRules(field)"
                      >
                        <v-select
                          :id="field.key"
                          v-model="form.state"
                          :options="stateOptions"
                          :reduce="option => option.id"
                          label="name"
                          :placeholder="field.placeholder || 'Select the state'"
                          autocomplete="nope"
                          @input="fetchCityList()"
                        >
                          <template #option="option">
                            {{ option.name }}
                          </template>
                        </v-select>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </ValidationProvider>
                    </b-form-group>

                    <!-- Special Case 3: City Dropdown (Dependent) -->
                    <b-form-group
                      v-else-if="field.key === 'city_id'"
                      :id="'group-' + field.key"
                      :label-for="field.key"
                    >
                      <template #label>
                        <span>{{ field.label }}</span>
                        <span
                          v-if="field.required"
                          class="text-danger"
                        > *</span>
                      </template>
                      <ValidationProvider
                        #default="{ errors }"
                        :name="field.label"
                        :rules="getFieldValidationRules(field)"
                      >
                        <v-select
                          :id="field.key"
                          v-model="form.city"
                          :options="cityOptions"
                          :reduce="option => option.id"
                          label="name"
                          :placeholder="field.placeholder || 'Select the city'"
                          autocomplete="nope"
                        >
                          <template #option="option">
                            {{ option.name }}
                          </template>
                        </v-select>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </ValidationProvider>
                    </b-form-group>

                    <!-- Special Case 4: Merchant Type Radio -->
                    <b-form-group
                      v-else-if="field.key === 'merchant_type'"
                      :id="'group-' + field.key"
                      :label-for="field.key"
                      class="text-center"
                    >
                      <ValidationProvider
                        #default="{ errors }"
                        :name="field.label"
                        :rules="getFieldValidationRules(field)"
                      >
                        <b-form-radio-group
                          :id="field.key"
                          v-model="merchantTypeSelected"
                          :options="getRadioOptions(field)"
                          class="mb-1"
                          value-field="value"
                          text-field="text"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </ValidationProvider>
                    </b-form-group>

                    <!-- Special Case 5: Remarks / Description Textarea with counter -->
                    <b-form-group
                      v-else-if="field.key === 'description'"
                      :id="'group-' + field.key"
                      :label-for="field.key"
                    >
                      <template #label>
                        <span>{{ field.label }}</span>
                        <span
                          v-if="field.required"
                          class="text-danger"
                        > *</span>
                      </template>
                      <ValidationProvider
                        #default="{ errors }"
                        :name="field.label"
                        :rules="getFieldValidationRules(field)"
                      >
                        <b-form-textarea
                          :id="field.key"
                          v-model="form.description"
                          :placeholder="field.placeholder || 'Enter remarks'"
                          :class="form.description.length >= maxChar ? 'text-danger' : ''"
                          :maxlength="maxChar"
                          :state="errors.length > 0 ? false : (form.description.length < maxChar ? null : false)"
                          style="overflow: auto; scrollbar-width: none;"
                          :rows="3"
                        />
                        <small
                          class="textarea-counter-value float-right"
                          :class="form.description.length >= maxChar ? 'bg-danger' : ''"
                        >
                          <span class="char-count">{{ form.description.length }}</span> / {{ maxChar }}
                        </small>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </ValidationProvider>
                    </b-form-group>

                    <!-- Standard Document Uploads -->
                    <b-form-group
                      v-else-if="field.type === 'file'"
                      :id="'group-' + field.key"
                      :label-for="field.key"
                    >
                      <!-- Inline Label + Tooltip Icon (Never Overlaps) -->
                      <template #label>
                        <div class="d-inline-flex align-items-center">
                          <span>{{ field.label }}</span>
                          <span
                            v-if="field.required"
                            class="text-danger mr-25"
                          > *</span>
                          <feather-icon
                            :id="'tooltip-file-' + field.key"
                            icon="InfoIcon"
                            size="14"
                            class="text-muted ml-50 cursor-pointer"
                          />
                          <b-tooltip
                            :target="'tooltip-file-' + field.key"
                            triggers="hover"
                          >
                            {{ verificationDocTip }}
                          </b-tooltip>
                        </div>
                      </template>

                      <ValidationProvider
                        #default="{ errors }"
                        :name="field.label"
                        :rules="getFieldValidationRules(field)"
                      >
                        <div class="position-relative">
                          <b-form-file
                            :id="field.key"
                            :value="getFileFieldValue(field.key)"
                            accept=".pdf,image/*"
                            :placeholder="field.placeholder || 'Attach image/pdf'"
                            drop-placeholder="Drop file here..."
                            @change="handleFileChange(field.key, $event)"
                          />
                          <div
                            v-if="getFileFieldValue(field.key)"
                            class="upload-cancel"
                            @click="clearFileField(field.key)"
                          >
                            <feather-icon icon="XIcon" />
                          </div>
                        </div>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </ValidationProvider>
                    </b-form-group>

                    <!-- Text / Number / Email / Password Inputs -->
                    <b-form-group
                      v-else-if="field.type === 'text' || field.type === 'number'"
                      :id="'group-' + field.key"
                      :label-for="field.key"
                    >
                      <template #label>
                        <span>{{ field.label }}</span>
                        <span
                          v-if="field.required"
                          class="text-danger"
                        > *</span>
                      </template>
                      <ValidationProvider
                        #default="{ errors }"
                        :name="field.label"
                        :rules="getFieldValidationRules(field)"
                      >
                        <b-form-input
                          :id="field.key"
                          :value="getFieldValue(field.key)"
                          :type="getInputHtmlType(field)"
                          :placeholder="field.placeholder || ''"
                          :state="errors.length > 0 ? false : null"
                          @input="setFieldValue(field.key, $event)"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </ValidationProvider>
                    </b-form-group>

                    <!-- Dropdown / Select Inputs -->
                    <b-form-group
                      v-else-if="field.type === 'select'"
                      :id="'group-' + field.key"
                      :label-for="field.key"
                    >
                      <template #label>
                        <span>{{ field.label }}</span>
                        <span
                          v-if="field.required"
                          class="text-danger"
                        > *</span>
                      </template>
                      <ValidationProvider
                        #default="{ errors }"
                        :name="field.label"
                        :rules="getFieldValidationRules(field)"
                      >
                        <v-select
                          :id="field.key"
                          :value="getFieldValue(field.key)"
                          :options="field.options || []"
                          :placeholder="field.placeholder || 'Select option'"
                          @input="setFieldValue(field.key, $event)"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </ValidationProvider>
                    </b-form-group>

                    <!-- Custom Radio Inputs -->
                    <b-form-group
                      v-else-if="field.type === 'radio'"
                      :id="'group-' + field.key"
                      :label-for="field.key"
                    >
                      <template #label>
                        <span>{{ field.label }}</span>
                        <span
                          v-if="field.required"
                          class="text-danger"
                        > *</span>
                      </template>
                      <ValidationProvider
                        #default="{ errors }"
                        :name="field.label"
                        :rules="getFieldValidationRules(field)"
                      >
                        <b-form-radio-group
                          :id="field.key"
                          :value="getFieldValue(field.key)"
                          :options="getRadioOptions(field)"
                          class="my-1"
                          @input="setFieldValue(field.key, $event)"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </ValidationProvider>
                    </b-form-group>

                    <!-- Custom Checkbox Inputs -->
                    <b-form-group
                      v-else-if="field.type === 'checkbox'"
                      :id="'group-' + field.key"
                      :label-for="field.key"
                    >
                      <ValidationProvider
                        #default="{ errors }"
                        :name="field.label"
                        :rules="getFieldValidationRules(field)"
                      >
                        <b-form-checkbox
                          :id="field.key"
                          :checked="getFieldValue(field.key)"
                          @change="setFieldValue(field.key, $event)"
                        >
                          {{ field.label }}
                          <span
                            v-if="field.required"
                            class="text-danger"
                          > *</span>
                        </b-form-checkbox>
                        <small class="text-danger">{{ errors[0] }}</small>
                      </ValidationProvider>
                    </b-form-group>

                    <!-- Generic Textarea Inputs -->
                    <b-form-group
                      v-else-if="field.type === 'textarea'"
                      :id="'group-' + field.key"
                      :label-for="field.key"
                    >
                      <template #label>
                        <span>{{ field.label }}</span>
                        <span
                          v-if="field.required"
                          class="text-danger"
                        > *</span>
                      </template>
                      <ValidationProvider
                        #default="{ errors }"
                        :name="field.label"
                        :rules="getFieldValidationRules(field)"
                      >
                        <b-form-textarea
                          :id="field.key"
                          :value="getFieldValue(field.key)"
                          :placeholder="field.placeholder || ''"
                          rows="3"
                          :state="errors.length > 0 ? false : null"
                          @input="setFieldValue(field.key, $event)"
                        />
                        <small class="text-danger">{{ errors[0] }}</small>
                      </ValidationProvider>
                    </b-form-group>
                  </b-col>
                </b-row>
              </div>

              <!-- Terms and Conditions & Submit Button Section -->
              <b-row class="d-flex flex-column flex-md-row align-items-center mt-2">
                <b-col
                  cols="12"
                  class="d-flex flex-wrap justify-content-center align-items-center mb-md-2"
                >
                  <div
                    v-if="merchantTerms && activeTermsId"
                    class="d-flex align-items-center mb-2 mb-md-0 add-margin-bottom justify-content-center"
                  >
                    <b-form-group class="mb-0 mr-1">
                      <b-form-checkbox
                        id="accept-terms"
                        v-model="status"
                        name="checkbox-1"
                      />
                    </b-form-group>
                    <b-card-text class="mb-0 mr-1">
                      <span>I accept the </span>
                      <b-link
                        href="#"
                        @click.prevent="openTermsAndConditions"
                      >
                        <span>&nbsp;Terms & Conditions</span>
                      </b-link>
                    </b-card-text>
                  </div>
                </b-col>

                <b-col
                  cols="12"
                  class="d-flex flex-wrap justify-content-center align-items-center mb-1 mb-md-0"
                >
                  <div class="d-flex align-items-center mb-2 mb-md-0 add-margin-bottom justify-content-center">
                    <b-button
                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                      type="submit"
                      variant="primary"
                      :disabled="isSubmitDisabled"
                      class="mr-2"
                    >
                      Submit
                    </b-button>
                    <b-button
                      v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                      type="reset"
                      variant="outline-secondary"
                      class="mr-1"
                      @click="clearForm"
                    >
                      Clear
                    </b-button>
                  </div>
                </b-col>
              </b-row>
            </b-form>
          </ValidationObserver>
        </b-overlay>
      </b-card>
    </div>
  </div>
</template>

<script>
import {
  BFormRadioGroup,
  BButton,
  BCard,
  BCol,
  BFormGroup,
  BForm,
  BFormInput,
  BFormTextarea,
  BRow,
  BOverlay,
  VBModal,
  BLink,
  BFormCheckbox,
  BCardText,
  BFormFile,
  BTooltip,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import Ripple from 'vue-ripple-directive'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import vSelect from 'vue-select'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'

const MerchantSelfRepository = RepositoryFactory.get('SelfRegister')
const MerchantRepository = RepositoryFactory.get('merchantSetting')

export default {
  directives: {
    'b-modal': VBModal,
    Ripple,
  },
  components: {
    BFormRadioGroup,
    BButton,
    BCard,
    BCol,
    BFormGroup,
    BForm,
    BFormInput,
    BFormTextarea,
    BRow,
    BOverlay,
    BLink,
    BFormCheckbox,
    BCardText,
    BFormFile,
    'v-select': vSelect,
    BTooltip,
    FeatherIcon,
    ValidationProvider,
    ValidationObserver,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return {
      modalLoading: false,
      configLoading: false,
      loading: false,

      // Form builder dynamic configuration
      configuredFields: [],
      configuredSections: [],

      // Form values model
      form: {
        name: '',
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        postalCode: '',
        country: null,
        state: null,
        city: null,
        contactNo: '',
        email: '',
        description: '',
        username: '',
        password: '',
        firstName: '',
        lastName: '',
        userEmail: '',
        userContactNo: '',
        accountName: '',
        accountNo: '',
        bankName: '',
        branchName: '',
        branchCode: '',
        merchantVerifyDoc: null,
        userVerifyDoc: null,
        bankDetailsVerifyDoc: null,
        custom_fields: {},
      },

      cityOptions: [],
      stateOptions: [],
      countryOptions: [],
      merchantTypeSelected: 'credit',
      merchantTypeOptions: [
        { text: 'Cash', value: 'cash', disabled: false },
        { text: 'Credit', value: 'credit', disabled: false },
      ],
      merchantType: false,
      merchantVerifyDocEnable: false,
      merchantTerms: false,
      status: false,
      activeTermsId: null,
      verificationDocTip: 'Ensure your file is 2MB or less before uploading',
      maxChar: 750,
    }
  },
  computed: {
    isSubmitDisabled() {
      if (!this.merchantTerms) {
        return false
      }
      if (this.merchantTerms && this.activeTermsId === null) {
        return false
      }
      if (this.merchantTerms && this.activeTermsId !== null) {
        return !this.status
      }
      return true
    },

    // Return unique sections that contain at least one visible field
    visibleSections() {
      const active = []
      this.configuredFields
        .filter(f => this.isFieldVisible(f))
        .forEach(f => {
          const { section } = f
          const sec = section || 'Additional Details'
          if (!active.includes(sec)) {
            active.push(sec)
          }
        })
      return active
    },
  },
  async mounted() {
    this.modalLoading = true
    await Promise.all([
      this.fetchConfiguration(),
      this.fetchCountryList(),
      this.getMerchantSetting(),
      this.getActiveTermsAndCondition(),
    ])
    this.modalLoading = false
  },
  methods: {
    // ================= DYNAMIC CONFIGURATION FETCHING =================
    async fetchConfiguration() {
      this.configLoading = true
      try {
        const response = await MerchantSelfRepository.getConfiguration()
        const { data: responseBody } = response
        const configData = responseBody.data ? responseBody.data : responseBody
        const { fields = [], sections = [] } = configData

        this.configuredSections = sections
        this.configuredFields = fields.map(f => {
          const { section, width, type } = f
          let colWidth = width
          if (!colWidth) {
            colWidth = (type === 'textarea' || f.key === 'business_name' || f.key === 'description') ? 'full' : 'half'
          }
          return {
            ...f,
            section: this.normalizeSectionName(section),
            width: colWidth,
            enabled: Boolean(f.enabled),
            required: Boolean(f.required),
            options: Array.isArray(f.options) ? f.options : [],
          }
        })
      } catch (err) {
        this.convertAndNotifyError(err)
      } finally {
        this.configLoading = false
      }
    },

    isVerificationDocField(key) {
      return [
        'merchant_verify_doc',
        'user_verify_doc',
        'bank_details_verify_doc',
      ].includes(key)
    },

    // Check visibility against both form builder config and tenant feature settings
    isFieldVisible(field) {
      if (!field.enabled) {
        return false
      }
      // If merchant_type is toggled off in tenant settings, hide it
      if (field.key === 'merchant_type' && !this.merchantType) {
        return false
      }
      // If verification doc uploads are toggled off in tenant settings, hide them
      if (this.isVerificationDocField(field.key) && !this.merchantVerifyDocEnable) {
        return false
      }
      return true
    },

    normalizeSectionName(section) {
      if (!section) return 'Business Details'
      if (section === 'Merchant Primary Business Details') return 'Business Details'
      if (section === 'Merchant Admin Details') return 'Admin Details'
      return section
    },

    getSectionDisplayTitle(section) {
      if (section === 'Business Details') return 'Merchant Primary Business Details'
      if (section === 'Admin Details') return 'Merchant Admin Details'
      if (section === 'Merchant Type') return 'Merchant Type'
      return section
    },

    getSectionIcon(section) {
      if (section === 'Business Details') return 'BriefcaseIcon'
      if (section === 'Admin Details') return 'UserIcon'
      if (section === 'Bank Details') return 'DollarSignIcon'
      if (section === 'Merchant Type') return 'CheckCircleIcon'
      return 'FileTextIcon'
    },

    getEnabledFieldsBySection(sectionName) {
      return this.configuredFields
        .filter(f => f.section === sectionName && this.isFieldVisible(f))
        .sort((a, b) => (a.order || 0) - (b.order || 0))
    },

    getFieldColSpan(field) {
      const { width, type, key } = field
      if (width === 'full' || type === 'textarea' || key === 'business_name' || key === 'description' || key === 'merchant_type') {
        return 12
      }
      return 6
    },

    getInputHtmlType(field) {
      const { key, type } = field
      if (key === 'password') return 'password'
      if (key === 'email' || key === 'user_email') return 'email'
      if (type === 'number') return 'number'
      return 'text'
    },

    getFieldValidationRules(field) {
      const { required, key, type } = field
      const isEmail = key === 'email' || key === 'user_email' || type === 'email'

      // Verification doc rules: only required if enabled in tenant settings AND marked required in form
      if (this.isVerificationDocField(key)) {
        return (required && this.merchantVerifyDocEnable) ? 'required' : ''
      }

      if (required) {
        return isEmail ? 'required|email' : 'required'
      }
      return isEmail ? 'email' : ''
    },

    getRadioOptions(field) {
      const { options } = field
      if (Array.isArray(options) && options.length > 0) {
        return options.map(opt => ({ text: opt, value: opt.toLowerCase() }))
      }
      return this.merchantTypeOptions
    },

    // ================= DYNAMIC DATA MODEL GET/SET =================
    getFieldModelKey(key) {
      const keyMap = {
        business_name: 'name',
        address_line_1: 'addressLine1',
        address_line_2: 'addressLine2',
        address_line_3: 'addressLine3',
        postal_code: 'postalCode',
        country_id: 'country',
        state_id: 'state',
        city_id: 'city',
        contact_no: 'contactNo',
        email: 'email',
        description: 'description',
        username: 'username',
        password: 'password',
        first_name: 'firstName',
        last_name: 'lastName',
        user_email: 'userEmail',
        user_contact_no: 'userContactNo',
        account_name: 'accountName',
        account_no: 'accountNo',
        bank_name: 'bankName',
        branch_name: 'branchName',
        branch_code: 'branchCode',
        merchant_verify_doc: 'merchantVerifyDoc',
        user_verify_doc: 'userVerifyDoc',
        bank_details_verify_doc: 'bankDetailsVerifyDoc',
      }
      return keyMap[key] || null
    },

    getFieldValue(key) {
      const standardKey = this.getFieldModelKey(key)
      if (standardKey) {
        return this.form[standardKey]
      }
      return this.form.custom_fields[key] || ''
    },

    setFieldValue(key, value) {
      const standardKey = this.getFieldModelKey(key)
      if (standardKey) {
        this.form[standardKey] = value
      } else {
        this.$set(this.form.custom_fields, key, value)
      }
    },

    getFileFieldValue(key) {
      const standardKey = this.getFieldModelKey(key)
      if (standardKey) {
        return this.form[standardKey]
      }
      return this.form.custom_fields[key] || null
    },

    handleFileChange(key, event) {
      const { target } = event
      const { files } = target
      const file = files[0]
      if (!file) return

      const maxSizeInMB = 2
      const maxSizeInBytes = maxSizeInMB * 1024 * 1024
      if (file.size > maxSizeInBytes) {
        this.showErrorMessage(`File size exceeds ${maxSizeInMB} MB. Please upload a smaller file.`)
        target.value = ''
        return
      }

      const standardKey = this.getFieldModelKey(key)
      if (standardKey) {
        this.form[standardKey] = file
      } else {
        this.$set(this.form.custom_fields, key, file)
      }
    },

    clearFileField(key) {
      const standardKey = this.getFieldModelKey(key)
      if (standardKey) {
        this.form[standardKey] = null
      } else {
        this.$set(this.form.custom_fields, key, null)
      }
    },

    // ================= LOCATION APIS =================
    async fetchCityList() {
      this.form.city = null
      try {
        this.loading = true
        const { state } = this.form
        if (state === null || state === undefined || state === '') {
          this.cityOptions = []
        } else {
          const { data } = (await MerchantSelfRepository.getCityListByID(state)).data
          this.cityOptions = data
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      } finally {
        this.loading = false
      }
    },

    async fetchStateList() {
      this.form.state = null
      this.form.city = null
      try {
        const { country } = this.form
        if (country !== undefined && country !== null) {
          const { data } = (await MerchantSelfRepository.getStateListByID(country)).data
          this.stateOptions = data
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },

    async fetchCountryList() {
      try {
        const { data } = (await MerchantSelfRepository.getCountryList()).data
        this.countryOptions = data
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },

    // ================= SUBMISSION =================
    async onClickCreateMerchant() {
      this.modalLoading = true
      try {
        let verifyDocResponse = null

        // Check if either default OR custom files exist
        const hasDefaultFiles = Boolean(
          this.form.merchantVerifyDoc || this.form.bankDetailsVerifyDoc || this.form.userVerifyDoc,
        )
        const hasCustomFiles = this.configuredFields.some(
          f => f.type === 'file' && f.group === 'custom' && this.form.custom_fields[f.key] instanceof File,
        )

        if (hasDefaultFiles || hasCustomFiles) {
          verifyDocResponse = await this.uploadVerificationDocuments()
          if (verifyDocResponse.status !== 200) {
            this.showErrorMessage('Verification document upload failed')
            this.modalLoading = false
            return
          }
        }

        // Get all uploaded URLs returned from backend
        const uploadedDocs = verifyDocResponse?.data?.data || {}

        // Replace File objects in custom_fields with their uploaded URL strings
        const cleanedCustomFields = {}
        Object.keys(this.form.custom_fields || {}).forEach(k => {
          const fieldConfig = this.configuredFields.find(f => f.key === k)

          if (fieldConfig && fieldConfig.type === 'file') {
            // Use returned URL string if uploaded; otherwise null
            cleanedCustomFields[k] = uploadedDocs[k] || null
          } else if (this.form.custom_fields[k] === '') {
            cleanedCustomFields[k] = null
          } else {
            cleanedCustomFields[k] = this.form.custom_fields[k]
          }
        })

        const payload = {
          name: this.form.name,
          business_name: this.form.name,
          description: this.form.description,
          merchant_verify_doc: uploadedDocs.merchant_verify_doc || null,
          bank_details: {
            account_name: this.form.accountName,
            account_number: this.form.accountNo,
            bank_name: this.form.bankName,
            branch_name: this.form.branchName,
            branch_code: this.form.branchCode,
            bank_details_verify_doc: uploadedDocs.bank_details_verify_doc || null,
          },
          user_details: {
            first_name: this.form.firstName,
            last_name: this.form.lastName,
            email: this.form.userEmail,
            user_name: this.form.username,
            phone: this.form.userContactNo,
            password: this.form.password,
            user_verify_doc: uploadedDocs.user_verify_doc || null,
          },
          address_details: {
            address_line_1: this.form.addressLine1,
            address_line_2: this.form.addressLine2,
            address_line_3: this.form.addressLine3,
            city_id: this.form.city,
            state_id: this.form.state,
            phone: this.form.contactNo,
            country_id: this.form.country,
            postal_code: this.form.postalCode,
            type: 'general',
            is_default: true,
          },
          contact_details: {
            phone: this.form.contactNo,
            email: this.form.email,
          },
          business_setting: {
            merchant_type: this.merchantType ? this.merchantTypeSelected : 'credit',
          },
          // 👈 Send cleaned custom fields containing the URL string
          custom_fields: cleanedCustomFields,
          ...(this.merchantTerms ? { terms_and_condition_id: this.activeTermsId } : {}),
        }

        await MerchantSelfRepository.setMerchant(payload)
        this.showSuccessMessage('Your account has been created successfully, please contact your courier service provider to activate your account')
        await this.$router.push('/login')
      } catch (e) {
        this.convertAndNotifyError(e)
      } finally {
        this.modalLoading = false
      }
    },

    async getMerchantSetting() {
      try {
        const { data } = (await MerchantRepository.getMerchantSettingsResource()).data
        this.merchantType = Boolean(data.merchant_type_select)
        this.merchantTerms = Boolean(data.terms_and_condition_enabale)
        this.merchantVerifyDocEnable = Boolean(data.verify_document_upload_enable)
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },

    async getActiveTermsAndCondition() {
      try {
        const { data } = (await MerchantSelfRepository.getActiveTermsAndCondition()).data
        if (data) {
          this.activeTermsId = data.id
          this.termsContent = data.content
        } else {
          this.activeTermsId = null
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      }
    },

    openTermsAndConditions() {
      if (this.termsContent && this.termsContent.startsWith('https') && this.termsContent.endsWith('.pdf')) {
        window.open(this.termsContent, '_blank')
      } else {
        const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Terms & Conditions</title>
          <link
            href="https://cdnjs.cloudflare.com/ajax/libs/bootstrap/4.6.0/css/bootstrap.min.css"
            rel="stylesheet"
          />
        </head>
        <body>
          <div class="container mt-5">
            <div class="card">
              <div class="card-body px-5">
                ${this.termsContent}
                <div class="card mt-2 border-0 shadow-none print-button-section">
                  <div class="card-body d-flex justify-content-center">
                    <button class="print-button btn btn-outline-success" style="display: inline-block;" onclick="window.print()">Print</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <style>
            @media print {
              .print-button-section {
                display: none !important;
              }
              .card-body {
                box-sizing: border-box;
                padding: 20px !important;
              }
              .card {
                box-sizing: border-box;
                border: 1px solid #c7c7cd !important;
                margin: 0 !important;
                max-width: 100%;
              }
              body {
                margin: 0;
                padding: 0;
              }
              .container {
                max-width: 100%;
                padding: 0 15px;
              }
            }
          </style>
        </body>
        </html>
      `
        const blob = new Blob([htmlContent], { type: 'text/html' })
        const url = URL.createObjectURL(blob)
        window.open(url, '_blank')
      }
    },

    async uploadVerificationDocuments() {
      const formData = new FormData()

      // 1. Append default files
      formData.append('merchant_verify_doc', this.form.merchantVerifyDoc || '')
      formData.append('bank_details_verify_doc', this.form.bankDetailsVerifyDoc || '')
      formData.append('user_verify_doc', this.form.userVerifyDoc || '')

      // 2. Append any custom file fields
      this.configuredFields.forEach(field => {
        if (field.type === 'file' && field.group === 'custom') {
          const file = this.form.custom_fields[field.key]
          if (file instanceof File) {
            formData.append(field.key, file)
          }
        }
      })

      return MerchantSelfRepository.updateVerificationDocuments(formData)
    },

    clearForm() {
      this.form = {
        name: '',
        addressLine1: '',
        addressLine2: '',
        addressLine3: '',
        postalCode: '',
        country: null,
        state: null,
        city: null,
        contactNo: '',
        email: '',
        description: '',
        username: '',
        password: '',
        firstName: '',
        lastName: '',
        userEmail: '',
        userContactNo: '',
        accountName: '',
        accountNo: '',
        bankName: '',
        branchName: '',
        branchCode: '',
        merchantVerifyDoc: null,
        userVerifyDoc: null,
        bankDetailsVerifyDoc: null,
        custom_fields: {},
      }
      this.merchantTypeSelected = 'credit'
      this.status = false
      if (this.$refs.createMerchantForm) {
        this.$refs.createMerchantForm.reset()
      }
    },
  },
}
</script>

<style scoped>
@import "../../../node_modules/@syncfusion/ej2-base/styles/fabric.css";
@import "../../../node_modules/@syncfusion/ej2-vue-navigations/styles/fabric.css";
@import "../../../node_modules/@syncfusion/ej2-inputs/styles/fabric.css";
@import "../../../node_modules/@syncfusion/ej2-buttons/styles/fabric.css";
</style>
<style lang="scss" scoped>
@import '@core/scss/base/pages/page-auth.scss';
.card-title {
  display: flex;
  justify-content: center;
  font-size: 1.5rem !important;
  text-align: center;
}
.sub-heading {
  text-align: center;
}
.add-margin-bottom {
  margin-bottom: 20px;
}
@media (max-width: 767px) {
  .add-margin-bottom {
    width: 100%;
  }
}
.upload-cancel {
  position: absolute;
  right: 81px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  z-index: 10;
}
.position-relative {
  position: relative;
}
</style>
